/* istanbul ignore file */
/**
 * Host-page loader for the payout-calculator popup.
 *
 * This is the ONLY thing a host page includes:
 *
 *   <script src="https://lead-form.maxretail.app/payout-popup.js" defer></script>
 *
 * No container div, no CSS, no inline JS — deliberately. The previous
 * implementation lived in a Divi code module on WordPress, where `wpautop`
 * rewrote a JavaScript string literal into a syntax error and Divi's renderer
 * stripped `<p>` tags out of the markup. Anything we ask a CMS to store gets
 * mangled eventually, so the host page stores one script tag and nothing else.
 *
 * Responsibilities, all of which used to be WordPress plugin config:
 *   1. decide whether this visitor should see the popup at all (suppression),
 *   2. trigger on a delay or on exit intent,
 *   3. build a fixed overlay + iframe, sized from the widget's own messages,
 *   4. forward the host page's GA4 client id / gclid (see attribution.ts),
 *   5. own the modal semantics the iframe cannot: scroll lock, focus trap,
 *      Escape, backdrop click, focus restoration,
 *   6. relay the widget's funnel events to the host page so its tags can fire.
 *
 * Optional attributes on the script tag:
 *   data-src            — override the widget URL (defaults to this script's origin)
 *   data-delay          — ms before auto-open (default 5000; 0 disables the timer)
 *   data-suppress-days  — days to stay hidden after a dismissal (default 7)
 *   data-no-exit-intent — present = don't open on exit intent
 */
import { withAttribution } from './attribution.js'

const SCRIPT = document.currentScript as HTMLScriptElement | null
const SCRIPT_SRC = SCRIPT?.src

const DEFAULT_DELAY_MS = 5000
const DEFAULT_SUPPRESS_DAYS = 7
/** Give the widget this long to report a height before we reveal the overlay. */
const REVEAL_TIMEOUT_MS = 1200
/** Let the widget record why it closed before we tear the iframe down. */
const TEARDOWN_GRACE_MS = 200

/**
 * Storage keys are namespaced and versioned, deliberately NOT reusing the
 * WordPress popup's `mr_cpop_*` keys. Those were frequently set by a bug that
 * marked visitors dismissed after a failed open, so honouring them would keep
 * hiding this from people who never actually saw it.
 */
const DISMISSED_KEY = 'mr-payout-calc:dismissed-at'
const CONVERTED_KEY = 'mr-payout-calc:converted'
/** Set by `?mr_payout=preview` — lets internal testers see the popup on
 * production ahead of the SSM flag being turned on for everyone. */
const PREVIEW_KEY = 'mr-payout-calc:preview'

/** How long the flag-config fetch gets before we give up and stay hidden. */
const CONFIG_FETCH_TIMEOUT_MS = 1500

const numberAttr = (raw: string | undefined, fallback: number): number => {
  if (raw === undefined) {
    return fallback
  }
  const parsed = Number(raw)
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback
}

/** localStorage throws in private modes / when disabled — never let that break the page. */
const readStore = (key: string): string => {
  try {
    return window.localStorage.getItem(key) ?? ''
  } catch {
    return ''
  }
}

const writeStore = (key: string, value: string): void => {
  try {
    window.localStorage.setItem(key, value)
  } catch {
    // no-op: suppression is a nicety, not a requirement
  }
}

const widgetUrl = (): string => {
  const override = SCRIPT?.dataset.src
  if (override) {
    return override
  }
  return SCRIPT_SRC
    ? new URL('payout-calculator.html', SCRIPT_SRC).href
    : 'payout-calculator.html'
}

const suppressDays = (): number =>
  numberAttr(SCRIPT?.dataset.suppressDays, DEFAULT_SUPPRESS_DAYS)

/**
 * Where to check the kill-switch flag. No host page ever sets this — every
 * environment names its widget host `lead-form.<apex>` and its API host
 * `lead-form-api.<apex>` (see infra/src/stacks/lead-form-widget.ts), so it's
 * derived from the script's own origin the same way `widgetUrl` derives the
 * calculator page. `data-config-url` exists only as an escape hatch (e.g.
 * local dev, where that naming convention doesn't hold).
 */
const configUrl = (): string => {
  const override = SCRIPT?.dataset.configUrl
  if (override) {
    return override
  }
  if (!SCRIPT_SRC) {
    return ''
  }
  try {
    const url = new URL(SCRIPT_SRC)
    const apiHost = url.hostname.replace(/^lead-form\./, 'lead-form-api.')
    if (apiHost === url.hostname) {
      return ''
    }
    url.hostname = apiHost
    url.pathname = '/payout/config'
    url.search = ''
    return url.href
  } catch {
    return ''
  }
}

/**
 * The popup script is embedded sitewide (global header), but the popup itself
 * is home-page-only. Tolerant of a trailing slash and any query/hash, so both
 * `https://host/` and `https://host` (with `?utm=...` etc.) count.
 */
const isHomePage = (): boolean =>
  window.location.pathname.replace(/\/+$/, '') === ''

/**
 * Is this visitor an internal tester previewing the feature ahead of the
 * SSM flag being turned on? `?mr_payout=preview` marks the browser permanently
 * (mirrors `?mr=c` above) so testers don't need the query param on every page.
 */
const isPreview = (): boolean => {
  if (/[&?]mr_payout=preview(?:&|$)/.test(window.location.search)) {
    writeStore(PREVIEW_KEY, '1')
    return true
  }
  return readStore(PREVIEW_KEY) === '1'
}

/**
 * Server-side kill switch / rollout flag. Fails closed: any network error,
 * timeout, or non-200 leaves the popup hidden rather than risk showing a
 * half-configured feature because the flag check itself broke.
 */
const isEnabled = async (): Promise<boolean> => {
  const url = configUrl()
  if (!url) {
    return false
  }
  try {
    const res = await fetch(url, {
      signal: AbortSignal.timeout(CONFIG_FETCH_TIMEOUT_MS),
    })
    if (!res.ok) {
      return false
    }
    const data = (await res.json()) as { enabled?: boolean }
    return data.enabled === true
  } catch {
    return false
  }
}

/**
 * Has this visitor already converted, or dismissed us recently?
 *
 * `?mr=c` on the landing URL marks the visitor permanently converted — it's used
 * in emails to existing customers so the popup never interrupts them.
 */
const isSuppressed = (): boolean => {
  if (/[&?]mr=c(?:&|$)/.test(window.location.search)) {
    writeStore(CONVERTED_KEY, '1')
    return true
  }
  if (readStore(CONVERTED_KEY) === '1') {
    return true
  }
  const dismissedAt = Number(readStore(DISMISSED_KEY))
  if (!Number.isFinite(dismissedAt) || dismissedAt <= 0) {
    return false
  }
  const days = (Date.now() - dismissedAt) / 86_400_000
  return days < suppressDays()
}

/**
 * Focus trap. The widget lives in a cross-origin iframe, so we can't see or
 * manage its internal tab order — we can only stop Tab escaping past the
 * boundary. Two tabbable sentinels either side bounce focus back to the frame.
 */
const sentinel = (onFocus: () => void): HTMLElement => {
  const element = document.createElement('div')
  element.tabIndex = 0
  element.setAttribute('aria-hidden', 'true')
  element.style.cssText = 'position:absolute;width:1px;height:1px;opacity:0;'
  element.addEventListener('focus', onFocus)
  return element
}

interface IPopup {
  open: () => void
}

const createPopup = (): IPopup => {
  const url = withAttribution(new URL(widgetUrl(), window.location.href))
  let overlay: HTMLDivElement | undefined
  let frame: HTMLIFrameElement | undefined
  let lastFocused: Element | undefined
  let revealTimer = 0
  let scrollY = 0

  const lockScroll = (): void => {
    scrollY = window.scrollY
    // Compensate for the scrollbar so the page doesn't jump sideways.
    const gap = window.innerWidth - document.documentElement.clientWidth
    document.documentElement.style.overflow = 'hidden'
    if (gap > 0) {
      document.body.style.paddingRight = `${gap}px`
    }
  }

  const unlockScroll = (): void => {
    document.documentElement.style.overflow = ''
    document.body.style.paddingRight = ''
    window.scrollTo({ top: scrollY })
  }

  const close = (reason: string): void => {
    if (!overlay) {
      return
    }
    writeStore(DISMISSED_KEY, String(Date.now()))
    // Tell the widget why, so it can record the dismissal, then give it a
    // moment to flush before the iframe goes away.
    try {
      frame?.contentWindow?.postMessage(
        { type: 'mr-payout-calc-closing', reason },
        url.origin
      )
    } catch {
      // cross-origin teardown races are not worth failing over
    }
    const dying = overlay
    overlay = undefined
    frame = undefined
    dying.style.opacity = '0'
    window.setTimeout(() => {
      dying.remove()
      unlockScroll()
      if (lastFocused instanceof HTMLElement) {
        lastFocused.focus()
      }
    }, TEARDOWN_GRACE_MS)
    document.removeEventListener('keydown', onKeydown, true)
  }

  const onKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      event.stopPropagation()
      close('escape')
    }
  }

  const reveal = (): void => {
    window.clearTimeout(revealTimer)
    if (overlay) {
      overlay.style.opacity = '1'
      frame?.focus()
    }
  }

  const onMessage = (event: MessageEvent): void => {
    if (
      event.origin !== url.origin ||
      !frame ||
      event.source !== frame.contentWindow
    ) {
      return
    }
    const data = event.data as { type?: string; height?: number } | undefined
    if (
      data?.type === 'mr-payout-calc-height' &&
      typeof data.height === 'number'
    ) {
      frame.style.height = `${data.height}px`
      reveal()
    }
    if (data?.type === 'mr-payout-calc-close') {
      close('widget')
    }
    if (data?.type === 'mr-payout-calc-event') {
      // Same shape the lead form uses, so host-page tag managers can listen for
      // either widget with one handler.
      window.dispatchEvent(
        new CustomEvent('mr-payout-calc-event', { detail: event.data })
      )
    }
  }

  const open = (): void => {
    if (overlay) {
      return
    }
    lastFocused = document.activeElement ?? undefined

    overlay = document.createElement('div')
    overlay.style.cssText = [
      'position:fixed',
      'inset:0',
      'z-index:2147483000',
      'display:flex',
      'align-items:center',
      'justify-content:center',
      'padding:16px',
      'background:rgba(3,26,34,0.72)',
      '-webkit-backdrop-filter:blur(4px)',
      'backdrop-filter:blur(4px)',
      'opacity:0',
      'transition:opacity 180ms ease',
    ].join(';')
    overlay.addEventListener('click', (event) => {
      if (event.target === overlay) {
        close('backdrop')
      }
    })

    frame = document.createElement('iframe')
    frame.src = url.href
    frame.title = 'Payout calculator'
    frame.setAttribute('scrolling', 'no')
    frame.style.cssText = [
      'width:100%',
      'max-width:560px',
      'height:620px',
      'max-height:calc(100vh - 32px)',
      'border:0',
      'border-radius:20px',
      'display:block',
      'background:transparent',
    ].join(';')

    const top = sentinel(() => frame?.focus())
    const bottom = sentinel(() => frame?.focus())
    overlay.append(top, frame, bottom)
    document.body.append(overlay)

    lockScroll()
    document.addEventListener('keydown', onKeydown, true)
    window.addEventListener('message', onMessage)
    // Reveal even if the widget never reports a height, so a failed load shows
    // something closable rather than a permanently invisible modal.
    revealTimer = window.setTimeout(reveal, REVEAL_TIMEOUT_MS)
  }

  return { open }
}

const start = async (): Promise<void> => {
  // Guard against the tag being included twice (a global header AND a page
  // module, say) — two overlays would trap focus against each other.
  const flag = '__mrPayoutCalcLoaded'
  const globals = window as unknown as Record<string, boolean>
  if (globals[flag]) {
    return
  }
  globals[flag] = true

  // Never open inside another frame: that would be the widget embedding itself.
  if (window.self !== window.top) {
    return
  }

  // The script tag is embedded sitewide (global header), but the popup is
  // scoped to the home page only — checked first since it's free (no
  // storage/network) and everything below is wasted effort elsewhere.
  if (!isHomePage()) {
    return
  }

  // Internal preview testers skip the flag check entirely; everyone else is
  // gated by the SSM-backed kill switch (fails closed — see isEnabled).
  if (!isPreview() && !(await isEnabled())) {
    return
  }

  if (isSuppressed()) {
    return
  }

  const popup = createPopup()
  let opened = false
  const openOnce = (): void => {
    if (!opened) {
      opened = true
      popup.open()
    }
  }

  const delay = numberAttr(SCRIPT?.dataset.delay, DEFAULT_DELAY_MS)
  if (delay > 0) {
    window.setTimeout(openOnce, delay)
  }

  if (SCRIPT?.dataset.noExitIntent === undefined) {
    document.addEventListener('mouseout', (event) => {
      // Pointer left through the top of the viewport, and not into a child
      // element or another window (relatedTarget is set for in-page moves).
      if (event.clientY <= 0 && !event.relatedTarget) {
        openOnce()
      }
    })
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => void start())
} else {
  void start()
}
